import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type { ActivityId, DisciplineId, FloorId, ProjectId } from '../shared/ids';
import { STRUCTURE_ERROR, STRUCTURE_ERROR_MESSAGE } from './errors';

/**
 * Situação de execução da atividade.
 *
 * Risco e impedimento **não** aparecem aqui, de propósito (ADR-010): são
 * `ActivityRestriction`, entidade separada, porque uma atividade pode estar `IN_PROGRESS`
 * **e** impedida ao mesmo tempo — que é exatamente o caso que a gestão precisa enxergar.
 * Colocá-los no status implicaria, falsamente, que uma atividade impedida parou.
 */
export type ActivityStatus = 'PLANNED' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';

export const ACTIVITY_STATUS_LABEL: Readonly<Record<ActivityStatus, string>> = {
  PLANNED: 'Planejada',
  IN_PROGRESS: 'Em execução',
  DONE: 'Concluída',
  CANCELLED: 'Cancelada',
};

/**
 * Transições permitidas.
 *
 * Tabela de constantes em vez de `if` encadeado: a regra fica legível de uma olhada e o
 * teste consegue percorrer a matriz inteira.
 */
const ALLOWED_TRANSITIONS: Readonly<Record<ActivityStatus, readonly ActivityStatus[]>> = {
  PLANNED: ['IN_PROGRESS', 'DONE', 'CANCELLED'],
  IN_PROGRESS: ['DONE', 'PLANNED', 'CANCELLED'],
  // Reabrir uma atividade concluída é legítimo — retrabalho acontece em obra.
  DONE: ['IN_PROGRESS'],
  CANCELLED: ['PLANNED'],
};

export function canTransition(from: ActivityStatus, to: ActivityStatus): boolean {
  return from === to || (ALLOWED_TRANSITIONS[from]?.includes(to) ?? false);
}

export function allowedTransitionsFrom(status: ActivityStatus): readonly ActivityStatus[] {
  return ALLOWED_TRANSITIONS[status] ?? [];
}

/** Atividade encerrada não recebe alocação de mão de obra (RN-084, Fase 7). */
export function isClosed(status: ActivityStatus): boolean {
  return status === 'DONE' || status === 'CANCELLED';
}

/**
 * Atividade — a interseção Pavimento × Disciplina (ADR-008).
 *
 * É o coração operacional do sistema: é o que se planeja, o que recebe mão de obra, o que
 * entra em risco e o que aparece na conferência ("Alvenaria — 3º Pavimento").
 *
 * Existe no máximo uma por par `(floorId, disciplineId)` (RN-004).
 */
export interface Activity {
  readonly id: ActivityId;
  readonly projectId: ProjectId;
  readonly floorId: FloorId;
  readonly disciplineId: DisciplineId;
  readonly status: ActivityStatus;
  readonly plannedStart?: string;
  readonly plannedEnd?: string;
  readonly actualStart?: string;
  readonly actualEnd?: string;
  readonly progressPct?: number;
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export interface CreateActivityInput {
  readonly id: ActivityId;
  readonly projectId: ProjectId;
  readonly floorId: FloorId;
  readonly disciplineId: DisciplineId;
  readonly now: string;
  readonly notes?: string;
}

export function createActivity(input: CreateActivityInput): Result<Activity, DomainError> {
  return ok({
    id: input.id,
    projectId: input.projectId,
    floorId: input.floorId,
    disciplineId: input.disciplineId,
    status: 'PLANNED',
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  });
}

/** Chave natural da atividade (RN-004). */
export function activityNaturalKey(floorId: FloorId, disciplineId: DisciplineId): string {
  return `${floorId}|${disciplineId}`;
}

export function activityExistsError(): DomainError {
  return domainError(
    STRUCTURE_ERROR.ACTIVITY_EXISTS,
    STRUCTURE_ERROR_MESSAGE[STRUCTURE_ERROR.ACTIVITY_EXISTS]!,
  );
}

/**
 * Muda a situação, respeitando a matriz de transições.
 *
 * `RN-008` mora aqui por omissão: não existe função que altere `floorId` ou
 * `disciplineId`. A identidade da atividade é imutável — corrigir um erro de estrutura
 * significa remover e recriar, o que preserva a coerência do histórico de alocações.
 */
export function changeActivityStatus(
  activity: Activity,
  next: ActivityStatus,
  now: string,
): Result<Activity, DomainError> {
  if (!canTransition(activity.status, next)) {
    return err(
      domainError(
        STRUCTURE_ERROR.INVALID_STATUS_TRANSITION,
        `Não é possível mudar de "${ACTIVITY_STATUS_LABEL[activity.status]}" para "${ACTIVITY_STATUS_LABEL[next]}".`,
        { from: activity.status, to: next },
      ),
    );
  }
  if (activity.status === next) return ok(activity);
  return ok({ ...activity, status: next, updatedAt: now });
}

export interface ActivityScheduleUpdate {
  readonly plannedStart?: string | null;
  readonly plannedEnd?: string | null;
  readonly notes?: string | null;
}

/**
 * Atualiza período previsto e observações.
 *
 * `null` remove o valor; `undefined` deixa como está. `plannedEnd` nunca pode ser
 * anterior a `plannedStart` (RN-022).
 */
export function updateActivitySchedule(
  activity: Activity,
  update: ActivityScheduleUpdate,
  now: string,
): Result<Activity, DomainError> {
  const plannedStart =
    update.plannedStart === undefined ? activity.plannedStart : (update.plannedStart ?? undefined);
  const plannedEnd =
    update.plannedEnd === undefined ? activity.plannedEnd : (update.plannedEnd ?? undefined);

  if (plannedStart !== undefined && plannedEnd !== undefined && plannedEnd < plannedStart) {
    return err(
      domainError(
        STRUCTURE_ERROR.INVALID_LEVEL_RANGE,
        'O fim previsto não pode ser anterior ao início previsto.',
        { plannedStart, plannedEnd },
      ),
    );
  }

  const notes = update.notes === undefined ? activity.notes : (update.notes ?? undefined);

  return ok({
    id: activity.id,
    projectId: activity.projectId,
    floorId: activity.floorId,
    disciplineId: activity.disciplineId,
    status: activity.status,
    createdAt: activity.createdAt,
    updatedAt: now,
    ...(plannedStart === undefined ? {} : { plannedStart }),
    ...(plannedEnd === undefined ? {} : { plannedEnd }),
    ...(activity.actualStart === undefined ? {} : { actualStart: activity.actualStart }),
    ...(activity.actualEnd === undefined ? {} : { actualEnd: activity.actualEnd }),
    ...(activity.progressPct === undefined ? {} : { progressPct: activity.progressPct }),
    ...(notes === undefined ? {} : { notes }),
    ...(activity.deletedAt === undefined ? {} : { deletedAt: activity.deletedAt }),
  });
}
